<script setup lang="ts">
import {
  UiAsyncCombobox,
  UiCheckbox,
  UiFileUpload,
  UiFormField,
  UiInputField,
  UiRichTextEditor,
  UiSelect,
  UiTextarea,
  UiText,
  useMediaUploadIntegration,
} from '@shopexperts/shared';
import type { ComboboxItemData, PageSchemaField, PartnerBadge } from '@shopexperts/shared';
import { computed, ref, watch } from 'vue';
import { badgeToComboboxItem, useBadgeSearch } from '../../composables/useBadgeSearch';
import { useBadge } from '../../composables/useBadge';
import { useCategories, type ServiceCategory } from '../../composables/useCategories';
import { useCountries } from '../../composables/useCountries';
import MetafieldKeyCopyButton from '../shared/MetafieldKeyCopyButton.vue';
import ExpertsSmartFilterField from '../experts-smart-filter/ExpertsSmartFilterField.vue';
import LinkGroupsField from '../link-groups/LinkGroupsField.vue';
import { uploadRichTextImage } from '../../utils/richTextImageEmbed';
import { entitySelectValue, normalizeEntityId } from '../../utils/metafieldValues';
import { normalizeSuperCollectionPreset } from '../../utils/superCollectionPreset';

const props = defineProps<{
  field: PageSchemaField;
}>();

const model = defineModel<Record<string, any>>({ required: true });

const { uploadFn, deleteFn } = useMediaUploadIntegration();
const badgeSearchQuery = ref('');
const selectedBadge = ref<PartnerBadge | null>(null);
const selectedCategory = ref<ServiceCategory | null>(null);

const { comboboxItems: badgeComboboxItems, isLoading: isBadgeSearching } = useBadgeSearch({
  search: badgeSearchQuery,
  status: 'active',
  limit: 20,
});

const {
  categoryOptions,
  subcategoryOptions,
  categoryById,
  subcategoryById,
  isLoading: isCategoriesLoading,
} = useCategories();

const { countryOptions } = useCountries();

const value = computed({
  get: () => model.value[props.field.key],
  set: (next) => {
    model.value = { ...model.value, [props.field.key]: next };
  },
});
const numberValue = computed<string>({
  get: () => {
    const current = model.value[props.field.key];
    return current === null || current === undefined ? '' : String(current);
  },
  set: (next) => {
    const trimmed = String(next).trim();
    const parsed = trimmed === '' ? null : Number(trimmed);
    value.value = parsed === null || Number.isNaN(parsed) ? null : parsed;
  },
});
// Super-collection preset filters render as the smart filter picker even on
// legacy schemas where the field is still typed `textarea`.
const isPresetFiltersField = computed(() => props.field.key === 'preset_filters_query');
const isSmartFilterField = computed(() => props.field.type === 'experts-smart-filter' || isPresetFiltersField.value);
const smartFilterError = computed(() => {
  if (!isPresetFiltersField.value) return undefined;
  const raw = typeof value.value === 'string' ? value.value.trim() : '';
  if (!raw) return 'Choose at least one expert filter.';
  return normalizeSuperCollectionPreset(value.value).errors[0];
});

const selectedBadgeId = computed(() => {
  const existing = model.value[props.field.key];
  const normalized = normalizeEntityId(existing);
  if (normalized) return normalized.toString();
  return selectedBadge.value?.id?.toString();
});

// Resolve the saved badge so an existing selection renders with its label and
// icon even when it is not part of the current search results. The input
// text mirrors what picking the badge by hand does in preserve mode, but only
// when the resolved badge itself changes — clearing the input to type a new
// search must never refill it with the current selection.
const draftBadgeId = computed(() => (
  props.field.type === 'partner_badge' ? normalizeEntityId(model.value[props.field.key]) : null
));
const { badge: existingBadge } = useBadge(draftBadgeId);
const badgeSelectedItems = ref<ComboboxItemData[]>([]);
let appliedBadgeLabel: string | null = null;

watch([draftBadgeId, existingBadge], () => {
  if (!draftBadgeId.value) {
    badgeSelectedItems.value = [];
    if (appliedBadgeLabel && badgeSearchQuery.value === appliedBadgeLabel) {
      badgeSearchQuery.value = '';
    }
    appliedBadgeLabel = null;
    return;
  }
  const resolved = selectedBadge.value && normalizeEntityId(selectedBadge.value.id) === draftBadgeId.value
    ? selectedBadge.value
    : (existingBadge.value && existingBadge.value.id === draftBadgeId.value ? existingBadge.value : null);
  if (!resolved) return;

  badgeSelectedItems.value = [badgeToComboboxItem(resolved)];
  if (resolved.name !== appliedBadgeLabel) {
    if (!badgeSearchQuery.value || badgeSearchQuery.value === appliedBadgeLabel) {
      badgeSearchQuery.value = resolved.name;
    }
    appliedBadgeLabel = resolved.name;
  }
}, { immediate: true });

const categoryValue = computed(() => {
  const existing = model.value[props.field.key];
  return entitySelectValue(existing);
});

const selectOptions = computed(() => (props.field.options || []).map((option) => ({ label: option, value: option })));

function onRichTextImageEmbed(file: File) {
  return uploadRichTextImage(uploadFn, file);
}

function onBadgeSelect(event: { value: string | string[]; metadata?: Record<string, unknown> | Record<string, unknown>[] }) {
  const metadata = Array.isArray(event.metadata) ? event.metadata[0] : event.metadata;
  const nextValue = Array.isArray(event.value) ? event.value[0] : event.value;
  selectedBadge.value = metadata as PartnerBadge;
  value.value = nextValue ? parseInt(nextValue, 10) : null;
}

function onCategoryChange(next: string | string[], type: 'service_category' | 'service_subcategory') {
  const stringValue = Array.isArray(next) ? next[0] : next;
  const numericValue = stringValue ? parseInt(stringValue, 10) : null;
  value.value = numericValue;

  if (!numericValue) {
    selectedCategory.value = null;
    return;
  }

  selectedCategory.value = type === 'service_category'
    ? categoryById.value[numericValue] || null
    : subcategoryById.value[numericValue] || null;
}
</script>

<template>
  <UiFormField :name="props.field.key" label="" :label-capitalize="false">
    <div class="flex flex-col gap-2">
      <div class="flex min-w-0 items-center gap-2">
        <UiText as="label" variant="label" class="min-w-0 truncate" :text="props.field.label" />
        <MetafieldKeyCopyButton :field-key="props.field.key" />
      </div>

      <ExpertsSmartFilterField
        v-if="isSmartFilterField"
        v-model="value"
        :name="props.field.key"
        :error="smartFilterError"
      />

      <LinkGroupsField
        v-else-if="props.field.type === 'link_groups'"
        v-model="value"
        :name="props.field.key"
      />

      <UiInputField
        v-else-if="props.field.type === 'text'"
        v-model="value"
        :placeholder="`Enter ${props.field.label.toLowerCase()}`"
      />

      <UiTextarea
        v-else-if="props.field.type === 'textarea'"
        v-model="value"
        :placeholder="`Enter ${props.field.label.toLowerCase()}`"
        :rows="4"
      />

      <UiRichTextEditor
        v-else-if="props.field.type === 'richtext'"
        v-model="value"
        :placeholder="`Enter ${props.field.label.toLowerCase()}`"
        :rows="6"
        resize="vertical"
        :on-image-embed="onRichTextImageEmbed"
        link-follow-controls
      />

      <UiInputField
        v-else-if="props.field.type === 'number'"
        v-model="numberValue"
        type="number"
        :placeholder="`Enter ${props.field.label.toLowerCase()}`"
      />

      <UiCheckbox
        v-else-if="props.field.type === 'boolean'"
        v-model="value"
        :label="props.field.label"
      />

      <UiSelect
        v-else-if="props.field.type === 'select'"
        v-model="value"
        :options="selectOptions"
        :placeholder="`Select ${props.field.label.toLowerCase()}`"
      />

      <UiFileUpload
        v-else-if="props.field.type === 'image'"
        v-model="value"
        :primary-text="`Upload ${props.field.label}`"
        secondary-text="Recommended: PNG or JPG format"
        :max-files="1"
        accept="image/*"
        :auto-upload="true"
        :upload-fn="uploadFn"
        :delete-fn="deleteFn"
        collection="page_metafield_images"
      />

      <UiAsyncCombobox
        v-else-if="props.field.type === 'partner_badge'"
        v-model="badgeSearchQuery"
        v-model:selected-items="badgeSelectedItems"
        :selected-id="selectedBadgeId"
        :data="badgeComboboxItems"
        :is-fetching="isBadgeSearching"
        display-mode="combobox"
        :show-selected-image="true"
        mode="preserve"
        :placeholder="`Select ${props.field.label.toLowerCase()}...`"
        @select="onBadgeSelect"
      />

      <UiSelect
        v-else-if="props.field.type === 'service_category'"
        :model-value="categoryValue"
        :options="categoryOptions"
        :placeholder="isCategoriesLoading ? 'Loading categories...' : `Select ${props.field.label.toLowerCase()}...`"
        :disabled="isCategoriesLoading"
        @update:model-value="onCategoryChange($event, 'service_category')"
      />

      <UiSelect
        v-else-if="props.field.type === 'service_subcategory'"
        :model-value="categoryValue"
        :options="subcategoryOptions"
        :placeholder="isCategoriesLoading ? 'Loading subcategories...' : `Select ${props.field.label.toLowerCase()}...`"
        :disabled="isCategoriesLoading"
        @update:model-value="onCategoryChange($event, 'service_subcategory')"
      />

      <UiSelect
        v-else-if="props.field.type === 'country'"
        v-model="value"
        :options="countryOptions"
        :placeholder="`Select ${props.field.label.toLowerCase()}...`"
      />

      <UiInputField
        v-else
        v-model="value"
        :placeholder="`Enter ${props.field.label.toLowerCase()}`"
      />

      <UiText
        v-if="props.field.required"
        as="p"
        variant="caption-normal"
        class="text-se-gray-700"
        text="Required field"
      />
    </div>
  </UiFormField>
</template>
